import { ListSourceType } from "@/enum/list.enum";

import { useGetLeanListsQuery } from "@/pages/Private/redux/list/list.api";

import { List } from "../schema/list";

interface UseTrackerDestinationListsOptions {
	/** Debounced search term typed into the picker. */
	searchValue: string;
	/** Job-change lists are trackers too — only the Companies/Competitors pickers leave them out. */
	includeJobChange?: boolean;
	/** Narrow to active lists, mirroring the picker that asked for it. */
	active?: boolean;
	/** Skip fetching entirely (e.g. while the picker's modal is closed). */
	skip?: boolean;
}

interface UseTrackerDestinationLists {
	lists: List[];
	isLoading: boolean;
	isFetching: boolean;
}

/**
 * The trackers a lead can be added to: TRACKER (and JOB_CHANGE) lists that are **not archived**.
 *
 * Archived lists are hidden from the Lead List Overview, so offering one as a destination sends
 * leads somewhere the user cannot see afterwards (PREC-3754). Every "add to tracker" surface shares
 * this hook so the filter can't drift apart again.
 */
export const useTrackerDestinationLists = ({
	searchValue,
	includeJobChange = true,
	active,
	skip,
}: UseTrackerDestinationListsOptions): UseTrackerDestinationLists => {
	const {
		data: trackerLists,
		isLoading: isLoadingTrackerLists,
		isFetching: isFetchingTrackerLists,
	} = useGetLeanListsQuery(
		{
			page: 1,
			limit: 15,
			searchValue,
			archived: false,
			sourceType: ListSourceType.TRACKER,
			...(active !== undefined && { active }),
		},
		{ skip, refetchOnMountOrArgChange: true }
	);

	const {
		data: jobChangeLists,
		isLoading: isLoadingJobChangeLists,
		isFetching: isFetchingJobChangeLists,
	} = useGetLeanListsQuery(
		{
			page: 1,
			limit: 15,
			searchValue,
			archived: false,
			sourceType: ListSourceType.JOB_CHANGE,
			...(active !== undefined && { active }),
		},
		{ skip: skip || !includeJobChange, refetchOnMountOrArgChange: true }
	);

	return {
		lists: [...(trackerLists?.data ?? []), ...(jobChangeLists?.data ?? [])],
		isLoading: isLoadingTrackerLists || isLoadingJobChangeLists,
		isFetching: isFetchingTrackerLists || isFetchingJobChangeLists,
	};
};
